import React, { useState, useEffect } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import {
  GraduationCap,
  MapPin,
  Search,
  Menu,
  X,
  ArrowRight,
  Users,
  BookOpen,
  Trophy,
  Globe,
  Phone,
  Mail,
  ChevronRight,
  Star,
  CheckCircle,
  Play,
  ChevronDown,
  BellRing,
  Megaphone,
  ExternalLink,
  Gift,
  FileText,
  Image as ImageIcon,
} from "lucide-react";

export default function HomePage() {
  const navigate = useNavigate();

  // ================= STATE MANAGEMENT =================
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [activeFaq, setActiveFaq] = useState(null);

  // --- Modals & Popups ---
  const [showModal, setShowModal] = useState(false);
  const [showNoticeBoard, setShowNoticeBoard] = useState(false);
  const [popupData, setPopupData] = useState(null);

  // --- Data States ---
  const [notices, setNotices] = useState([]);
  const [sliderIndex, setSliderIndex] = useState(0);
  const [heroCollege, setHeroCollege] = useState(null);
  const [featuredColleges, setFeaturedColleges] = useState([]);
  const [loading, setLoading] = useState(true);

  // --- Form State ---
  const [formData, setFormData] = useState({ name: "", phone: "", course: "" });
  const [isSubmitting, setIsSubmitting] = useState(false);

  // CONSTANTS
  const ADMIN_WHATSAPP_NUMBER = "919876543210";

  // Navigation
  const navLinks = [
    { name: "Home", path: "/" },

    { name: "Colleges", path: "/colleges" },
    { name: "About Us", path: "/about" },
    { name: "Services", path: "/services" },
    { name: "Contact", path: "/contact" },
  ];

  // ================= 1. API CALLS =================
  useEffect(() => {
    const fetchData = async () => {
      try {
        // A. Notices
        const updatesRes = await fetch(
          "https://map-your-college.siteflow.in/api/get_updates.php",
        );
        const updatesData = await updatesRes.json();
        if (Array.isArray(updatesData) && updatesData.length > 0) {
          setNotices(updatesData);
          setTimeout(() => setShowNoticeBoard(true), 2000);
        }

        // B. Colleges
        const collegesRes = await fetch(
          "https://map-your-college.siteflow.in/admin/get_home_colleges.php",
        );
        const collegesData = await collegesRes.json();
        if (collegesData) {
          setHeroCollege(collegesData.topPick || null);
          setFeaturedColleges(collegesData.list || []);
        }

        // C. Dynamic Popup
        const popupRes = await fetch(
          "https://map-your-college.siteflow.in/admin/get_popup.php",
        );
        const popupSettings = await popupRes.json();

        if (popupSettings && parseInt(popupSettings.is_active) === 1) {
          setPopupData(popupSettings);
          setTimeout(() => {
            setShowModal(true);
          }, 6000);
        }

        setLoading(false);
      } catch (error) {
        console.error("Failed to fetch data:", error);
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  // ================= 2. UI EFFECTS =================
  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 20);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

const originalTestimonials = [
{
id:1,
name:"Kapil Ved",
college:"MBA Student",
text:"The training program at Map Your College helped me understand how MBA interviews actually work. The mock personal interviews and detailed feedback improved my confidence and communication, which helped me secure admission to a good college."
},
{
id:2,
name:"Arnav Surana",
college:"MBA Aspirant",
text:"The mock interview sessions at Map Your College were extremely helpful. The mentors gave practical feedback and guided me on how to present my profile and career goals clearly during interviews."
},
{
id:3,
name:"Arav Gupta",
college:"MBA Aspirant",
text:"Before joining Map Your College, I was unsure about the MBA selection process. The structured training and interview practice helped me prepare better and confidently face the admission interviews."
},
{
id:4,
name:"Nidhi Jain",
college:"MBA Student",
text:"The personalized guidance at Map Your College helped me improve my interview skills and confidence. The mentors also guided me in choosing the right colleges according to my profile."
},
{
id:5,
name:"Tejaswi Mahajan",
college:"MBA Student",
text:"The mock PI sessions and detailed feedback helped me understand my strengths and areas of improvement. This preparation played a big role in helping me convert a good college."
},
{
id:6,
name:"Aditi Shastri",
college:"MBA Student",
text:"The training sessions at Map Your College helped me prepare for different interview questions and situations. The mentorship helped me present my profile more confidently."
},
{
id:7,
name:"Dhruv Varshney",
college:"MBA Aspirant",
text:"The interview preparation and guidance at Map Your College helped me understand what B-schools expect from candidates. The practice sessions really improved my confidence."
},
{
id:8,
name:"Aman Singhai",
college:"MBA Student",
text:"The mentors at Map Your College guided me step by step through the MBA admission process. The mock interviews helped me prepare effectively and perform better during the actual interview."
},
{
id:9,
name:"Piyush Sahu",
college:"MBA Student",
text:"The mock PI sessions and profile guidance helped me improve my answers and communication skills. This preparation helped me secure admission to a reputed college."
},
{
id:10,
name:"Ashish Hamad",
college:"MBA Aspirant",
text:"The structured training and mentorship at Map Your College helped me understand how to approach MBA interviews and present my goals clearly."
},
{
id:11,
name:"Shivam Patidar",
college:"MBA Student",
text:"The guidance and mock interviews at Map Your College helped me gain confidence and improve my interview performance. The feedback from mentors was extremely valuable."
},
{
id:12,
name:"Priyanshu Tawar",
college:"MBA Aspirant",
text:"Map Your College helped me prepare for the MBA selection process in a very practical way. The mock interviews and guidance improved my confidence and clarity."
},
{
id:13,
name:"Parth Chaturvedi",
college:"MBA Student",
text:"The training program at Map Your College helped me understand how to present my academic background and career goals during interviews."
},
{
id:14,
name:"Om Dutt Pandey",
college:"MBA Student",
text:"The mentorship and mock interview practice at Map Your College helped me prepare effectively for the MBA admission process."
},
{
id:15,
name:"Yash Singh",
college:"MBA Student",
text:"The mentors at Map Your College provided valuable feedback after every mock interview, which helped me improve my communication and confidence."
},
{
id:16,
name:"Shreya Yadav",
college:"MBA Student",
text:"The training sessions helped me understand how to answer interview questions confidently and present my profile in the best way."
},
{
id:17,
name:"Roshni Singh",
college:"MBA Student",
text:"Map Your College helped me gain confidence through interview practice and personalized mentorship, which helped me secure admission."
},
{
id:18,
name:"Harshit Rathore",
college:"MBA Aspirant",
text:"The mock interview sessions and guidance at Map Your College helped me prepare better for the MBA selection process."
},
{
id:19,
name:"Vinay Jhod",
college:"MBA Aspirant",
text:"The mentors at Map Your College helped me understand the interview expectations and guided me on how to present my profile effectively."
},
{
id:20,
name:"Kamlakshi Vyas",
college:"MBA Student",
text:"The training and mentorship at Map Your College helped me improve my confidence and prepare effectively for the MBA admission interviews."
}
];

const infiniteTestimonials = [
...originalTestimonials,
...originalTestimonials
];

useEffect(() => {
const slideTimer = setInterval(() => {
setSliderIndex((prev) =>
prev >= originalTestimonials.length ? 0 : prev + 1
);
}, 3000);

return () => clearInterval(slideTimer);
}, [originalTestimonials.length]);

  // ================= 3. HANDLERS =================
  const toggleFaq = (index) => setActiveFaq(activeFaq === index ? null : index);
  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) setShowModal(false);
  };

  const handleApplyNow = (collegeName) => {
    const message = `Hello, I am interested in admission at *${collegeName}*. Please provide more details.`;
    window.open(
      `https://wa.me/${ADMIN_WHATSAPP_NUMBER}?text=${encodeURIComponent(message)}`,
      "_blank",
    );
  };

  const handleInputChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleConsultationSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.phone) {
      Swal.fire({
        icon: "warning",
        title: "Missing Details",
        text: "Please enter Name and Phone.",
        confirmButtonColor: "#dc2626",
      });
      return;
    }
    setIsSubmitting(true);
    try {
      const response = await fetch(
        "https://map-your-college.siteflow.in/admin/save_lead.php",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(formData),
        },
      );
      const result = await response.json();
      if (result.status === "success") {
        Swal.fire({
          icon: "success",
          title: "Request Received!",
          text: "Our counselor will call you within 24 hours.",
          confirmButtonColor: "#dc2626",
        });
        setFormData({ name: "", phone: "", course: "" });
        setShowModal(false);
      } else {
        throw new Error(result.message);
      }
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Oops...",
        text: "Something went wrong.",
        confirmButtonColor: "#dc2626",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  // ================= 4. DYNAMIC POPUP RENDERER =================
  const renderPopupContent = () => {
    if (!popupData) return null;

    switch (popupData.type) {
      case "image":
        return (
          <div className="relative inline-block w-full">
            <button
              onClick={() => setShowModal(false)}
              className="absolute top-2 right-2 bg-black/60 text-white p-2 rounded-full hover:bg-red-600 transition-colors z-20 backdrop-blur-sm"
            >
              <X size={20} />
            </button>
            {popupData.image_url ? (
              <img
                src={popupData.image_url}
                alt="Notice"
                className="w-full h-auto max-h-[85vh] object-contain rounded-xl shadow-2xl block mx-auto"
              />
            ) : (
              <div className="p-10 text-center text-slate-400 bg-white rounded-xl">
                Image not found
              </div>
            )}
          </div>
        );
      case "text":
        return (
          <div className="bg-white p-8 text-center rounded-2xl relative shadow-2xl">
            <button
              onClick={() => setShowModal(false)}
              className="absolute top-4 right-4 text-slate-400 hover:text-red-600"
            >
              <X size={20} />
            </button>
            <Megaphone
              size={48}
              className="mx-auto text-blue-950 mb-4 bg-blue-100 p-3 rounded-full"
            />
            <h2 className="text-2xl font-bold text-blue-950 mb-3">
              {popupData.title}
            </h2>
            <p className="text-slate-600 mb-6 leading-relaxed">
              {popupData.description}
            </p>
            <button
              onClick={() => setShowModal(false)}
              className="bg-blue-950 text-white px-8 py-2 rounded-lg font-bold hover:bg-blue-900 transition w-full"
            >
              {popupData.btn_text || "OK, Got It"}
            </button>
          </div>
        );
      case "offer":
        return (
          <div className="bg-white rounded-2xl overflow-hidden relative shadow-2xl">
            <button
              onClick={() => setShowModal(false)}
              className="absolute top-3 right-3 bg-white/90 p-1 rounded-full text-slate-600 hover:text-red-600 z-10 shadow-sm"
            >
              <X size={20} />
            </button>
            {popupData.image_url && (
              <div className="h-48 overflow-hidden">
                <img
                  src={popupData.image_url}
                  alt="Offer"
                  className="w-full h-full object-cover"
                />
              </div>
            )}
            <div className="p-6 text-center">
              <h2 className="text-2xl font-bold text-blue-950 mb-2">
                {popupData.title}
              </h2>
              <p className="text-sm text-slate-600 mb-6">
                {popupData.description}
              </p>
              <a
                href={popupData.btn_link || "#"}
                target="_blank"
                rel="noreferrer"
                className="bg-green-600 text-white px-6 py-3 rounded-lg font-bold shadow-lg w-full block hover:bg-green-700 transition"
              >
                {popupData.btn_text || "View Offer"}
              </a>
            </div>
          </div>
        );
      case "form":
      default:
        return (
          <div className="bg-white rounded-2xl overflow-hidden relative shadow-2xl">
            <button
              onClick={() => setShowModal(false)}
              className="absolute top-4 right-4 text-white hover:text-red-200 z-10"
            >
              <X size={20} />
            </button>
            <div className="bg-blue-950 p-6 text-white relative overflow-hidden">
              <div className="absolute top-0 right-0 w-32 h-32 bg-red-600 rounded-full blur-3xl opacity-40"></div>
              <h2 className="text-2xl font-bold relative z-10">
                {popupData.title || "Free Counseling!"}
              </h2>
              <p className="text-blue-200 text-sm mt-1 relative z-10">
                {popupData.description || "Expert guidance for your career."}
              </p>
            </div>
            <div className="p-8">
              <h3 className="font-bold text-blue-950 text-lg mb-2">
                Confused about your career?
              </h3>
              <p className="text-slate-500 text-sm mb-6">
                Get a free 1-on-1 session with our senior experts.
              </p>
              <form className="space-y-4" onSubmit={handleConsultationSubmit}>
                <div>
                  <input
                    type="text"
                    name="name"
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-lg outline-none focus:border-blue-950 transition"
                    placeholder="Your Name"
                    required
                  />
                </div>
                <div>
                  <input
                    type="tel"
                    name="phone"
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-lg outline-none focus:border-blue-950 transition"
                    placeholder="Mobile Number"
                    required
                  />
                </div>

                {/* INTERESTED COURSE DROPDOWN ADDED BACK */}
                <div>
                  <select
                    name="course"
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-lg outline-none focus:border-blue-950 transition text-slate-500"
                    required
                    defaultValue=""
                  >
                    <option value="" disabled>
                      Select Interested Course
                    </option>
                    <option value="Engineering">Engineering (B.Tech)</option>
                    <option value="Medical">Medical (MBBS)</option>
                    <option value="Management">Management (MBA/BBA)</option>
                    <option value="Law">Law</option>
                    <option value="Study Abroad">Study Abroad</option>
                    <option value="Other">Other</option>
                  </select>
                </div>

                <button className="w-full bg-red-600 text-white py-3 rounded-lg font-bold hover:bg-red-700 shadow-lg shadow-red-200 transition flex justify-center items-center gap-2">
                  {isSubmitting
                    ? "Sending..."
                    : popupData.btn_text || "Book Free Session"}
                </button>
              </form>
              <button
                onClick={() => setShowModal(false)}
                className="text-xs text-slate-400 mt-4 underline hover:text-slate-600 w-full text-center block"
              >
                No thanks, I'll explore myself
              </button>
            </div>
          </div>
        );
    }
  };

  const faqs = [
    {
      q: "How do you select the right college?",
      a: "We use AI-driven profiling and 1-on-1 counseling.",
    },
    {
      q: "Do you help with education loans?",
      a: "Yes, we have tie-ups with major national banks.",
    },
    {
      q: "Is the initial consultation free?",
      a: "Absolutely. The first session is completely free.",
    },
    {
      q: "Can you help with admissions abroad?",
      a: "Yes, we handle admissions for USA, UK, Canada, etc.",
    },
  ];

  return (
    <div className="min-h-screen bg-white font-sans text-slate-800 selection:bg-red-100 selection:text-red-900 relative overflow-x-hidden">
      {/* HEADER */}
      <header
        className={`fixed w-full z-50 transition-all duration-300 border-b border-transparent ${isScrolled ? "bg-white/95 backdrop-blur-md shadow-sm border-slate-100 py-3" : "bg-transparent py-5"}`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex justify-between items-center">
          <div
            className="flex items-center gap-3 cursor-pointer group"
            onClick={() => navigate("/")}
          >
            <div className="relative">
              <GraduationCap
                className={`w-10 h-10 text-blue-950 transition-colors`}
                strokeWidth={2}
              />
              <div className="absolute top-[18px] left-1/2 -translate-x-1/2">
                <MapPin className="w-4 h-4 text-red-600 fill-red-600" />
              </div>
            </div>
            <div className="flex flex-col">
              <span className="text-2xl font-bold tracking-tight leading-none text-blue-950">
                Map<span className="text-blue-800">Your</span>College
              </span>
              <span className="text-[10px] font-bold tracking-widest text-slate-500 uppercase mt-1">
                Nurturing Dreams, Creating Leaders
              </span>
            </div>
          </div>
          <nav className="hidden lg:flex items-center gap-8">
            {navLinks.map((link) => (
              <NavLink
                key={link.name}
                to={link.path}
                className={({ isActive }) =>
                  `text-sm font-semibold transition-colors uppercase tracking-wide ${isActive ? "text-red-600 border-b-2 border-red-600 pb-1" : "text-slate-600 hover:text-red-600"}`
                }
              >
                {link.name}
              </NavLink>
            ))}
            <button
              onClick={() => navigate("/contact")}
              className="bg-red-600 text-white px-6 py-2.5 rounded text-sm font-bold hover:bg-red-700 transition-all shadow-lg"
            >
              Get Started
            </button>
          </nav>
          <button
            className="lg:hidden text-blue-950"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          >
            {mobileMenuOpen ? <X size={28} /> : <Menu size={28} />}
          </button>
        </div>
        {mobileMenuOpen && (
          <div className="absolute top-full left-0 w-full bg-white shadow-xl lg:hidden p-4 flex flex-col gap-4 animate-in slide-in-from-top-5">
            {navLinks.map((link) => (
              <NavLink
                key={link.name}
                to={link.path}
                onClick={() => setMobileMenuOpen(false)}
                className="text-lg font-semibold text-blue-950 py-2 border-b border-slate-50"
              >
                {link.name}
              </NavLink>
            ))}
            <button
              onClick={() => navigate("/contact")}
              className="bg-red-600 text-white w-full py-3 rounded font-bold"
            >
              Get Started
            </button>
          </div>
        )}
      </header>

      {/* HERO SECTION */}
      <section
        id="home"
        className="relative pt-24 pb-16 lg:pt-24 lg:pb-10 overflow-hidden bg-slate-50"
      >
        <div className="absolute top-0 right-0 w-1/2 h-full bg-blue-950 skew-x-12 translate-x-32 hidden lg:block"></div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 grid lg:grid-cols-2 gap-12 items-center">
          <div className="space-y-8">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-red-50 rounded-full border border-red-100">
              <span className="w-2 h-2 rounded-full bg-red-600 animate-pulse"></span>
              <span className="text-xs font-bold text-red-600 uppercase tracking-wider">
                Admissions Open 2025
              </span>
            </div>
            <h1 className="text-5xl lg:text-7xl font-extrabold text-blue-950 leading-[1.1]">
              Nurturing Dreams, <br />
              <span className="relative inline-block">
                Creating Leaders
                <svg
                  className="absolute w-full h-3 -bottom-1 left-0 text-red-600"
                  viewBox="0 0 200 9"
                  fill="none"
                >
                  <path
                    d="M2.00025 6.99997C25.7201 5.20035 83.245 -2.00013 197.965 2.75056"
                    stroke="currentColor"
                    strokeWidth="3"
                    strokeLinecap="round"
                  />
                </svg>
              </span>
            </h1>
            <p className="text-lg text-slate-600 max-w-lg leading-relaxed">
              Expert counseling, data-driven college mapping, and scholarship
              support.
            </p>
            <div className="bg-white p-2 rounded-lg shadow-xl border border-slate-100 flex flex-col sm:flex-row gap-2 max-w-xl">
              <div className="relative flex-grow">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 w-5 h-5" />
                <input
                  type="text"
                  placeholder="Search colleges..."
                  className="w-full h-12 pl-12 pr-4 rounded bg-slate-50 border-transparent focus:ring-2 focus:ring-blue-950 outline-none"
                />
              </div>
              <button className="h-12 px-8 bg-blue-950 text-white font-bold rounded hover:bg-blue-900 flex items-center justify-center gap-2">
                Search <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          </div>
          <div className="relative hidden lg:block h-[600px]">
            <div className="absolute top-10 right-10 w-64 h-64 border-[20px] border-white/10 rounded-full z-0"></div>
            {loading ? (
              <div className="relative z-10 bg-white p-6 rounded-2xl shadow-2xl border border-slate-100 max-w-sm ml-auto mt-12 animate-pulse h-96 w-80"></div>
            ) : heroCollege ? (
              <div className="relative z-10 bg-white p-6 rounded-2xl shadow-2xl border border-slate-100 max-w-sm ml-auto mt-12 transform rotate-3 hover:rotate-0 transition-all duration-500 cursor-pointer group">
                <div className="h-48 bg-slate-800 rounded-xl mb-6 relative overflow-hidden">
                  <img
                    src={heroCollege.image}
                    alt={heroCollege.name}
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent"></div>
                  <div className="absolute bottom-4 left-4 text-white">
                    <p className="text-xs font-medium bg-red-600 inline-block px-2 py-1 rounded mb-1">
                      Top Pick
                    </p>
                    <p className="font-bold text-lg">{heroCollege.name}</p>
                  </div>
                </div>
                <div className="space-y-4">
                  <div className="flex justify-between items-center text-sm border-b border-slate-100 pb-2">
                    <span className="text-slate-500">Avg Package</span>
                    <span className="font-bold text-blue-950">
                      {heroCollege.package}
                    </span>
                  </div>
                  <div className="flex justify-between items-center text-sm border-b border-slate-100 pb-2">
                    <span className="text-slate-500">Placement</span>
                    <span className="font-bold text-green-600">
                      {heroCollege.placement_rate}
                    </span>
                  </div>
                  <button
                    onClick={() => handleApplyNow(heroCollege.name)}
                    className="w-full py-2 border-2 border-blue-950 text-blue-950 font-bold rounded hover:bg-blue-950 hover:text-white transition-colors"
                  >
                    View Details
                  </button>
                </div>
              </div>
            ) : null}
          </div>
        </div>
      </section>

      {/* STATS */}
      <section className="bg-blue-950 py-10 text-white relative overflow-hidden">
        <div
          className="absolute inset-0 opacity-10"
          style={{
            backgroundImage:
              "radial-gradient(circle at 2px 2px, white 1px, transparent 0)",
            backgroundSize: "40px 40px",
          }}
        ></div>
        <div className="max-w-7xl mx-auto px-4 relative z-10">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center divide-x divide-white/10">
            {[
              { label: "Partner Colleges", val: "500+" },
              { label: "Students Placed", val: "12,000+" },
              { label: "Counselors", val: "50+" },
              { label: "Scholarships", val: "₹5Cr+" },
            ].map((stat, idx) => (
              <div key={idx} className="p-4">
                <h3 className="text-4xl lg:text-5xl font-extrabold text-white mb-2">
                  {stat.val}
                </h3>
                <p className="text-blue-200 text-sm font-medium tracking-wide uppercase">
                  {stat.label}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ABOUT US */}
      {/* ABOUT US */}
      <section id="about" className="py-10 px-4 bg-white relative">
        <div className="max-w-7xl mx-auto grid lg:grid-cols-2 gap-16 items-center">
          {/* Images */}
          <div className="relative">
            <div className="absolute -top-4 -left-4 w-24 h-24 bg-red-100 rounded-full z-0"></div>

            <div className="relative z-10 grid grid-cols-2 gap-4">
              <img
                src="https://images.unsplash.com/photo-1523240795612-9a054b0db644?auto=format&fit=crop&q=80&w=400"
                className="rounded-2xl shadow-lg w-full h-64 object-cover transform translate-y-8"
                alt="S1"
              />
              <img
                src="https://images.unsplash.com/photo-1531545514256-b1400bc00f31?auto=format&fit=crop&q=80&w=400"
                className="rounded-2xl shadow-lg w-full h-64 object-cover"
                alt="S2"
              />
            </div>
          </div>

          {/* Content */}
          <div>
            <span className="text-red-600 font-bold tracking-wider uppercase text-sm">
              Who We Are
            </span>

            <h2 className="text-3xl md:text-4xl font-extrabold text-blue-950 mt-2 mb-6">
              Map Your College
            </h2>

            <div className="space-y-2 mb-8">
              {[
                "Map Your College is a career training and student development platform focused on preparing students for college selection processes and professional careers.",
                "With 15+ years of experience, we specialize in selection process training for management and professional programs.",
                "We have successfully guided and trained 2000+ students for GD, PI, WAT, case studies, and extempore rounds.",
                "Our programs help students build communication skills, critical thinking, confidence, and interview readiness.",
                "We work with 100+ colleges in India and abroad to help students understand admission processes and career opportunities.",
                "Our mission is to prepare students to become future-ready professionals through practical training and career-focused learning.",
              ].map((item, i) => (
                <div key={i} className="flex items-start gap-3">
                  <div className="w-6 h-6 rounded-full bg-green-100 flex items-center justify-center mt-1">
                    <CheckCircle size={14} className="text-green-600" />
                  </div>

                  <span className="text-slate-600 leading-relaxed">{item}</span>
                </div>
              ))}
            </div>

            <button
              onClick={() => navigate("/about")}
              className="bg-slate-100 text-blue-950 px-8 py-3 rounded font-bold hover:bg-blue-950 hover:text-white transition-colors border border-slate-200"
            >
              Know More About Us
            </button>
          </div>
        </div>
      </section>

      {/* SERVICES */}
      <section id="services" className="py-10 px-4 bg-slate-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <span className="text-red-600 font-bold tracking-wider uppercase text-sm">
              Our Expertise
            </span>

            <h2 className="text-3xl md:text-4xl font-extrabold text-blue-950 mt-2">
              How We Help Students
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 grid-rows-2 gap-6 h-auto md:h-[600px]">
            {/* Career Clarity */}
            <div className="md:col-span-1 md:row-span-2 bg-white rounded-2xl p-8 border border-slate-100 hover:shadow-xl transition-all group relative overflow-hidden">
              <div className="absolute top-0 right-0 w-32 h-32 bg-blue-50 rounded-bl-full opacity-50"></div>

              <GraduationCap className="w-12 h-12 text-blue-950 mb-6 relative z-10" />

              <h3 className="text-2xl font-bold text-blue-950 mb-4 relative z-10">
                Career Clarity
              </h3>

              <p className="text-slate-600 mb-6 relative z-10">
                We help students make the right academic and career decisions
                through structured guidance.
              </p>

              <ul className="hidden md:block text-slate-600 space-y-2 mb-8 text-sm">
                <li className="flex items-start gap-2">
                  <span className="text-red-600 font-bold">•</span>
                  Identify the right career path based on interests and
                  strengths
                </li>

                <li className="flex items-start gap-2">
                  <span className="text-red-600 font-bold">•</span>
                  Guidance on programs and colleges that match the student
                  profile
                </li>

                <li className="flex items-start gap-2">
                  <span className="text-red-600 font-bold">•</span>
                  Personalized roadmap for admissions and preparation
                </li>

                <li className="flex items-start gap-2">
                  <span className="text-red-600 font-bold">•</span>
                  Expert mentorship to make confident career decisions
                </li>

                <li className="flex items-start gap-2">
                  <span className="text-red-600 font-bold">•</span>
                  Understand industry trends and future career opportunities
                </li>

                <li className="flex items-start gap-2">
                  <span className="text-red-600 font-bold">•</span>
                  Compare different colleges, courses, and specialization
                  options
                </li>
              </ul>

              <div className="absolute bottom-8 left-8">
                <button
                  onClick={() => navigate("/services")}
                  className="text-red-600 font-bold flex items-center gap-2 hover:gap-3 transition-all"
                >
                  Learn More <ArrowRight size={16} />
                </button>
              </div>
            </div>

            {/* Profile Evaluation */}
            <div className="md:col-span-2 bg-blue-950 rounded-2xl p-8 flex flex-col md:flex-row items-center justify-between text-white relative overflow-hidden">
              <div className="relative z-10 max-w-lg">
                <h3 className="text-2xl font-bold mb-2">Profile Evaluation</h3>

                <p className="text-blue-200">
                 We assess the student’s academic profile, communication skills, and overall readiness for competitive selection processes.
                </p>

                <p className="text-blue-200 mt-2 hidden md:block ">
                  Our experts also help identify strengths and areas of
                  improvement to better prepare students for admission
                  opportunities.
                </p>
              </div>

              <div className="mt-6 md:mt-0 bg-white/10 backdrop-blur-sm p-4 rounded-full">
                <Users className="w-8 h-8 text-yellow-400" />
              </div>
            </div>
            {/* Bottom 3 Services */}
            <div className="md:col-span-2 grid grid-cols-1 md:grid-cols-3 gap-6">
              {/* Selection Process Training */}
              <div className="bg-white border border-slate-200 rounded-2xl p-8 hover:border-red-200 hover:shadow-lg transition-all">
                <BookOpen className="w-10 h-10 text-red-600 mb-4" />
                <h3 className="text-xl font-bold text-blue-950 mb-2">
                  Selection Process Training
                </h3>
                <p className="text-sm text-slate-600">
                  Structured training for GD, PI, WAT, Case Studies, and
                  Extempore required by top colleges.
                </p>
              </div>

              {/* Practice & Mock Sessions */}
              <div className="bg-white border border-slate-200 rounded-2xl p-8 hover:border-blue-200 hover:shadow-lg transition-all">
                <Users className="w-10 h-10 text-blue-600 mb-4" />
                <h3 className="text-xl font-bold text-blue-950 mb-2">
                  Practice & Mock Sessions
                </h3>
                <p className="text-sm text-slate-600">
                  Regular mock interviews and group discussions to build
                  confidence and improve performance.
                </p>
              </div>

              {/* Final Preparation */}
              <div className="bg-white border border-slate-200 rounded-2xl p-8 hover:border-green-200 hover:shadow-lg transition-all">
                <Trophy className="w-10 h-10 text-green-600 mb-4" />
                <h3 className="text-xl font-bold text-blue-950 mb-2">
                  Final Preparation
                </h3>
                <p className="text-sm text-slate-600">
                  Focused mentoring and feedback to help students perform
                  confidently in final selection rounds.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
      {/* WHY STUDENTS NEED US */}
      <section className="py-10 px-4 bg-blue-950 text-white">
        <div className="max-w-5xl mx-auto text-left md:text-center">
          <h2 className="text-3xl md:text-4xl font-extrabold mb-6">
            Why Students Need Us
          </h2>

          <p className="text-blue-200 text-base md:text-lg leading-relaxed mb-10 max-w-3xl mx-auto md:mx-auto">
            Many students have strong academic potential but are not trained for
            the competitive selection process required by top colleges. From
            group discussions and interviews to case studies and extempore
            rounds, these stages require confidence, communication skills, and
            structured preparation. Our programs help students build these
            essential skills and prepare them to perform confidently during the
            final selection process.
          </p>

          <div className="grid md:grid-cols-2 gap-6 text-left max-w-4xl mx-auto">
            <div className="flex gap-3">
              <span className="text-red-400 text-xl mt-1">✓</span>
              <p className="text-blue-200">
                Most students lack structured preparation for GD, PI, WAT, and
                case study rounds.
              </p>
            </div>

            <div className="flex gap-3">
              <span className="text-red-400 text-xl mt-1">✓</span>
              <p className="text-blue-200">
                Limited exposure to real interview scenarios and selection
                processes.
              </p>
            </div>

            <div className="flex gap-3">
              <span className="text-red-400 text-xl mt-1">✓</span>
              <p className="text-blue-200">
                Lack of confidence and communication skills during competitive
                selection stages.
              </p>
            </div>

            <div className="flex gap-3">
              <span className="text-red-400 text-xl mt-1">✓</span>
              <p className="text-blue-200">
                Need for expert mentorship and practice sessions to perform
                better in final admission rounds.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* FEATURED COLLEGES */}
      {/* ================= FEATURED COLLEGES (MAX 6) ================= */}
      <section id="colleges" className="py-24 px-4 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="flex justify-between items-end mb-12">
            <div>
              <span className="text-red-600 font-bold uppercase tracking-wider text-sm">
                Top Institutes
              </span>
              <h2 className="text-3xl font-extrabold text-blue-950 mt-2">
                Partner Universities
              </h2>
            </div>
            <button
              onClick={() => navigate("/colleges")}
              className="hidden md:flex items-center gap-2 text-blue-950 font-bold hover:text-red-600 transition-colors"
            >
              View All Colleges <ArrowRight size={18} />
            </button>
          </div>

          {loading ? (
            <div className="text-center py-20 text-slate-500">
              Loading colleges...
            </div>
          ) : (
            <div className="grid md:grid-cols-3 gap-8">
              {featuredColleges.slice(0, 6).map((college, idx) => (
                <div
                  key={idx}
                  className="group bg-white rounded-xl shadow-lg border border-slate-100 overflow-hidden hover:-translate-y-2 transition-transform duration-300"
                >
                  <div className="h-48 bg-slate-200 relative overflow-hidden">
                    <img
                      src={college.image}
                      alt={college.name}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                    <div className="absolute top-4 right-4 bg-white/90 backdrop-blur px-3 py-1 rounded text-xs font-bold text-blue-950">
                      A+ Grade
                    </div>
                  </div>
                  <div className="p-6">
                    <div className="flex items-start justify-between mb-2">
                      <h3 className="text-xl font-bold text-blue-950">
                        {college.name}
                      </h3>
                    </div>
                    <div className="flex items-center gap-2 text-slate-500 text-sm mb-4">
                      <MapPin size={14} /> {college.location}
                    </div>
                    <div className="bg-slate-50 p-3 rounded-lg flex justify-between items-center mb-6">
                      <div>
                        <p className="text-xs text-slate-500 uppercase">
                          Avg Package
                        </p>
                        <p className="font-bold text-blue-950">
                          {college.package}
                        </p>
                      </div>
                      <div className="h-8 w-[1px] bg-slate-200"></div>
                      <div>
                        <p className="text-xs text-slate-500 uppercase">
                          Intake
                        </p>
                        <p className="font-bold text-blue-950">Aug 2025</p>
                      </div>
                    </div>
                    <button
                      onClick={() => handleApplyNow(college.name)}
                      className="w-full py-3 bg-blue-950 text-white font-bold rounded hover:bg-green-600 transition-colors flex items-center justify-center gap-2"
                    >
                      Apply Now <ArrowRight size={16} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}

          <div className="mt-8 text-center md:hidden">
            <button
              onClick={() => navigate("/colleges")}
              className="text-blue-950 font-bold border-b-2 border-red-600 pb-1"
            >
              View All Colleges
            </button>
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="py-24 px-4 bg-slate-50 overflow-hidden">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-extrabold text-blue-950 text-center">
              Stories of Success
            </h2>
            <p className="text-slate-500 mt-2">
              Hear from students who achieved their dreams.
            </p>
          </div>
          <div className="relative w-full overflow-hidden">
            <div
              className="flex transition-transform duration-500 ease-in-out"
              style={{
                transform: `translateX(-${sliderIndex * (100 / (window.innerWidth >= 768 ? 3 : 1))}%)`,
              }}
            >
              {infiniteTestimonials.map((t, index) => (
                <div
                  key={`${t.id}-${index}`}
                  className="min-w-full md:min-w-[33.333%] px-4"
                >
                  <div className="bg-white p-8 rounded-2xl relative shadow-sm border border-slate-100 hover:shadow-xl transition-all h-full flex flex-col">
                    <p className="text-slate-700 italic mb-6 leading-relaxed text-sm flex-grow">
                      "{t.text}"
                    </p>
                    <div className="flex items-center gap-4 mt-auto">
                      <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center font-bold text-blue-950 border border-blue-200">
                        {t.name.charAt(0)}
                      </div>
                      <div>
                        <h5 className="font-bold text-blue-950 text-sm">
                          {t.name}
                        </h5>
                        <p className="text-xs text-slate-500">{t.college}</p>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CONTACT & FAQ (Lead Form Updated) */}
      <section id="contact" className="py-20 px-4 bg-white">
        <div className="max-w-6xl mx-auto bg-blue-950 rounded-3xl overflow-hidden shadow-2xl flex flex-col md:flex-row">
          <div className="p-12 md:w-1/2 text-white relative">
            <div className="absolute top-0 right-0 w-64 h-64 bg-red-600 rounded-full blur-3xl opacity-20"></div>
            <h2 className="text-3xl font-bold mb-4 relative z-10">
              Get Your Free Career Roadmap
            </h2>
            <p className="text-blue-200 mb-8 relative z-10">
              Fill the form to book a free 30-minute consultation.
            </p>
            <ul className="space-y-4 relative z-10">
              <li className="flex items-center gap-3">
                <CheckCircle size={16} />
                <span>100% Free Consultation</span>
              </li>
              <li className="flex items-center gap-3">
                <CheckCircle size={16} />
                <span>Expert Guidance</span>
              </li>
            </ul>
          </div>

          {/* MAIN CONTACT FORM */}
          <div className="p-12 md:w-1/2 bg-white">
            <form className="space-y-4" onSubmit={handleConsultationSubmit}>
              <div>
                <input
                  type="text"
                  name="name"
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-lg outline-none focus:border-blue-950"
                  placeholder="John Doe"
                  required
                />
              </div>
              <div>
                <input
                  type="tel"
                  name="phone"
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-lg outline-none focus:border-blue-950"
                  placeholder="+91 98765 43210"
                  required
                />
              </div>

              {/* INTERESTED COURSE DROPDOWN ADDED BACK */}
              <div>
                <select
                  name="course"
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-lg outline-none focus:border-blue-950 transition text-slate-500"
                  required
                  defaultValue=""
                >
                  <option value="" disabled>
                    Select Interested Course
                  </option>
                  <option value="Engineering">Engineering (B.Tech)</option>
                  <option value="Medical">Medical (MBBS)</option>
                  <option value="Management">Management (MBA/BBA)</option>
                  <option value="Law">Law</option>
                  <option value="Study Abroad">Study Abroad</option>
                  <option value="Other">Other</option>
                </select>
              </div>

              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full py-4 bg-red-600 text-white font-bold rounded hover:bg-red-700 transition-colors shadow-lg disabled:opacity-70 flex justify-center items-center gap-2"
              >
                {isSubmitting ? "Booking..." : "Book Free Session"}
              </button>
            </form>
          </div>
        </div>
      </section>

      {/* FOOTER */}

      {/* FLOATING NOTICE BOARD */}
      <div className="fixed bottom-6 left-6 z-[55]">
        <button
          onClick={() => setShowNoticeBoard(!showNoticeBoard)}
          className="w-14 h-14 bg-red-600 text-white rounded-full flex items-center justify-center shadow-2xl hover:bg-red-700 transition-all hover:scale-110 animate-bounce relative"
        >
          <BellRing size={24} />{" "}
          {notices.length > 0 && (
            <span className="absolute top-0 right-0 w-4 h-4 bg-yellow-400 rounded-full border-2 border-white flex items-center justify-center text-[10px] text-black font-bold">
              {notices.length}
            </span>
          )}
        </button>
        {showNoticeBoard && notices.length > 0 && (
          <div className="absolute bottom-16 left-0 w-80 bg-white rounded-xl shadow-2xl border border-slate-100 overflow-hidden animate-in slide-in-from-bottom-10 fade-in duration-300">
            <div className="bg-blue-950 p-4 flex justify-between items-center text-white">
              <div className="flex items-center gap-2 font-bold">
                <Megaphone size={16} className="text-yellow-400" />
                <span>Latest Updates</span>
              </div>
              <button
                onClick={() => setShowNoticeBoard(false)}
                className="hover:bg-white/20 p-1 rounded"
              >
                <X size={16} />
              </button>
            </div>
            <div className="max-h-80 overflow-y-auto p-2 scrollbar-thin">
              {notices.map((notice) => (
                <div
                  key={notice.id}
                  className="p-3 border-b border-slate-50 hover:bg-slate-50 transition-colors cursor-pointer group"
                >
                  <div className="flex justify-between items-center mb-1">
                    <span
                      className={`text-[10px] font-bold px-2 py-0.5 rounded ${notice.type === "Exam" ? "bg-blue-100 text-blue-700" : notice.type === "Alert" ? "bg-red-100 text-red-700" : "bg-green-100 text-green-700"}`}
                    >
                      {notice.type}
                    </span>
                    <span className="text-[10px] text-slate-400 flex items-center gap-1">
                      {notice.date}
                    </span>
                  </div>
                  <p className="text-xs font-semibold text-slate-700 leading-snug group-hover:text-blue-950">
                    {notice.text}
                  </p>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* ================= DYNAMIC POPUP MODAL ================= */}
      {showModal && popupData && (
        <div
          className={`fixed inset-0 z-[60] flex bg-black/60 backdrop-blur-sm p-4 animate-in fade-in zoom-in duration-300 ${popupData.type === "image" ? "items-start justify-center pt-10" : "items-center justify-center"}`}
          onClick={handleBackdropClick}
        >
          <div
            className={`relative transform transition-all scale-100 shadow-2xl overflow-hidden ${popupData.type === "image" ? "bg-transparent shadow-none w-auto max-w-5xl" : "bg-white rounded-2xl w-full max-w-md"}`}
          >
            {renderPopupContent()}
          </div>
        </div>
      )}
    </div>
  );
}
